/**
 * ConsentBanner — DPDP-compliant consent modal
 *
 * Self-contained: no external runtime deps beyond React + Google Fonts.
 * Dark deep-space aesthetic with 3D glass card, mouse-tracking tilt,
 * 22-language selector with RTL support, and full DPDP integration.
 */

import { useState, useEffect, useRef, useCallback } from "react";
import { Globe, Lock, Check, ChevronDown, Search } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  grantConsent,
  withdrawConsent,
} from "@/services/dpdpApi";
import Cookies from "js-cookie";

// ---------------------------------------------------------------------------
// Translations — EN, HI, TA, MR, BN
// ---------------------------------------------------------------------------

const T = {
  en: {
    heading: "Your Privacy Choices",
    subheading: "Under the DPDP Act, 2023",
    acceptAllBtn: "Accept All & Continue",
    manageBtn: "Customise Preferences",
    rejectBtn: "Reject Non-Essential",
    backBtn: "Back",
    cancelBtn: "Cancel",
    customize: "Customise",
    acceptSelected: (n) => `Accept ${n} & Continue`,
    p0Label: "Account & Service Delivery",
    p0Desc: "Essential for platform access and core features",
    p1Label: "Usage Analytics",
    p1Desc: "Help us improve by sharing anonymous usage data",
    p2Label: "Marketing Communications",
    p2Desc: "Receive promos, updates and personalised recommendations",
    p3Label: "Personalisation",
    p3Desc: "Tailored experience based on your activity",
    retentionLabel: "Retention Period",
    retentionValue: "Until account deletion",
    locationLabel: "Data Location",
    locationValue: "Mumbai, India (ISO 27001)",
    thirdPartyLabel: "Third Parties",
    thirdPartyValue: "Only vetted vendors, signed DPA",
    privacyNotice: "Privacy Notice",
    noticeText:
      "We collect and process your personal data to provide platform services. You have the right to withdraw consent, access, correct, or erase your data, and raise grievances under the DPDP Act 2023.",
    selectLang: "Select language",
    searchPlaceholder: "Search language...",
    required: "Required",
    optional: "Optional",
  },
  hi: {
    heading: "आपकी गोपनीयता विकल्प",
    subheading: "DPDP अधिनियम, 2023 के तहत",
    acceptAllBtn: "सभी स्वीकारें & जारी रखें",
    manageBtn: "प्राथमिकताएँ अनुकूलित करें",
    rejectBtn: "गैर-आवश्यक अस्वीकारें",
    backBtn: "वापस",
    cancelBtn: "रद्द करें",
    customize: "अनुकूलित करें",
    acceptSelected: (n) => `${n} स्वीकारें & जारी रखें`,
    p0Label: "खाता और सेवा प्रदान करना",
    p0Desc: "प्लेटफ़ॉर्म एक्सेस और मुख्य सुविधाओं के लिए आवश्यक",
    p1Label: "उपयोग विश्लेषण",
    p1Desc: "हमें अज्ञात उपयोग डेटा साझा करके सुधारने में मदद करें",
    p2Label: "विपणन संचार",
    p2Desc: "प्रमोशन, अपडेट और व्यक्तिगत अनुशंसाएँ प्राप्त करें",
    p3Label: "व्यक्तिगतकरण",
    p3Desc: "आपकी गतिविधि के आधार पर अनुकूलित अनुभव",
    retentionLabel: "प्रतिधारण अवधि",
    retentionValue: "खाता हटाने तक",
    locationLabel: "डेटा स्थान",
    locationValue: "मुंबई, भारत (ISO 27001)",
    thirdPartyLabel: "तृतीय पक्ष",
    thirdPartyValue: "केवल सत्यापित विक्रेता, हस्ताक्षरित DPA",
    privacyNotice: "गोपनीयता सूचना",
    noticeText:
      "हम प्लेटफ़ॉर्म सेवाएँ प्रदान करने के लिए आपका व्यक्तिगत डेटा एकत्र और संसाधित करते हैं। आपको DPDP अधिनियम 2023 के तहत सहमति वापस लेने, डेटा एक्सेस, सुधार या मिटाने और शिकायत दर्ज कराने का अधिकार है।",
    selectLang: "भाषा चुनें",
    searchPlaceholder: "भाषा खोजें...",
    required: "आवश्यक",
    optional: "वैकल्पिक",
  },
  ta: {
    heading: "உங்கள் தனியுரிமை தேர்வுகள்",
    subheading: "DPDP சட்டம், 2023 கீழ்",
    acceptAllBtn: "எல்லாவற்றையும் ஏற்க & தொடரவும்",
    manageBtn: "விருப்பங்களை அமைக்கவும்",
    rejectBtn: "அவசியமற்றவை நிராகரிக்கவும்",
    backBtn: "மீண்டும்",
    cancelBtn: "ரத்து",
    customize: "தனிப்பயனாக்க",
    acceptSelected: (n) => `${n} ஏற்க & தொடரவும்`,
    p0Label: "கணக்கு மற்றும் சேவை வழங்கல்",
    p0Desc: "மேடையை அணுகவும் முக்கிய அம்சங்களுக்கு அவசியம்",
    p1Label: "பயன்பாட்டு பகுப்பாய்வு",
    p1Desc: "அநாமதேய பயன்பாட்டு தரவைப் பகிர்ந்து மேடையை மேம்படுத்த உதவுங்கள்",
    p2Label: "சந்தைப்படுத்தல் தகவல்தொடர்பு",
    p2Desc: "விளம்பரங்கள், புதுப்பிப்புகள் மற்றும் தனிப்பயன் பரிந்துரைகளைப் பெறவும்",
    p3Label: "தனிப்பயனாக்கம்",
    p3Desc: "உங்கள் செயல்பாட்டின் அடிப்படையில் தனிப்பயன் அனுபவம்",
    retentionLabel: "தக்கவைப்பு காலம்",
    retentionValue: "கணக்கு நீக்கம் வரை",
    locationLabel: "தரவு இடம்",
    locationValue: "மும்பை, இந்தியா (ISO 27001)",
    thirdPartyLabel: "மூன்றாம் தரப்பினர்",
    thirdPartyValue: "உறுதிப்படுத்தப்பட்ட விற்பனையாளர்கள் மட்டும்",
    privacyNotice: "தனியுரிமை அறிவிப்பு",
    noticeText:
      "மேடை சேவைகளை வழங்க உங்கள் தனிப்பட்ட தரவைச் சேகரிக்கிறோம். DPDP சட்டம் 2023 பிரகாரம் உங்களுக்கு சம்மதத்தைத் திரும்பப் பெற, தரவை அணுக, சரி செய்ய அல்லது அழிக்க மற்றும் புகாரை எழுப்ப உரிமை உண்டு.",
    selectLang: "மொழியைத் தேர்வுசெய்க",
    searchPlaceholder: "மொழியைத் தேடு...",
    required: "தேவையான",
    optional: "கட்டாயமற்ற",
  },
  mr: {
    heading: "तुमच्या गोपनीयता निवडी",
    subheading: "DPDP कायदा, 2023 अंतर्गत",
    acceptAllBtn: "सर्व स्वीकारा & पुढे जा",
    manageBtn: "प्राधान्ये बदला",
    rejectBtn: "गैर-आवश्यक नाकारा",
    backBtn: "मागे",
    cancelBtn: "रद्द करा",
    customize: "सानुकूलित करा",
    acceptSelected: (n) => `${n} स्वीकारा & पुढे जा`,
    p0Label: "खाते आणि सेवा वितरण",
    p0Desc: "प्लॅटफॉर्म प्रवेश आणि मुख्य वैशिष्ट्यांसाठी आवश्यक",
    p1Label: "वापर विश्लेषण",
    p1Desc: "अज्ञात वापर डेटा शेअर करून आम्हाला सुधारण्यास मदत करा",
    p2Label: "मार्केटिंग संवाद",
    p2Desc: "जाहिरात, अपडेट आणि वैयक्तिक शिफारसी प्राप्त करा",
    p3Label: "वैयक्तिकरण",
    p3Desc: "तुमच्या क्रियाकलापांवर आधारित सानुकूलित अनुभव",
    retentionLabel: "जतन कालावधी",
    retentionValue: "खाते नष्ट होईपर्यंत",
    locationLabel: "डेटा स्थान",
    locationValue: "मुंबई, भारत (ISO 27001)",
    thirdPartyLabel: "तृतीय पक्ष",
    thirdPartyValue: "केवळ विक्रेते, स्वाक्षरी DPA",
    privacyNotice: "गोपनीयता सूचना",
    noticeText:
      "आम्ही प्लॅटफॉर्म सेवा प्रदान करण्यासाठी तुमचा वैयक्तिक डेटा गोळा करतो आणि प्रक्रिया करतो. DPDP कायदा 2023 अंतर्गत तुम्हाला संमती मागे घेणे, डेटा प्रवेश, दुरुस्ती किंवा हटवणे आणि तक्रार नोंदवण्याचा अधिकार आहे.",
    selectLang: "भाषा निवडा",
    searchPlaceholder: "भाषा शोधा...",
    required: "आवश्यक",
    optional: "वैकल्पिक",
  },
  bn: {
    heading: "আপনার গোপনীয়তা পছন্দ",
    subheading: "DPDP আইন, 2023 অনুযায়ী",
    acceptAllBtn: "সব গ্রহণ করুন & এগিয়ে যান",
    manageBtn: "পছন্দগুলি সাজান",
    rejectBtn: "অ-প্রয়োজনীয় প্রত্যাখ্যান করুন",
    backBtn: "পিছনে",
    cancelBtn: "বাতিল",
    customize: "কাস্টমাইজ",
    acceptSelected: (n) => `${n}টি গ্রহণ & এগিয়ে যান`,
    p0Label: "অ্যাকাউন্ট ও সেবা প্রদান",
    p0Desc: "প্ল্যাটফর্ম অ্যাক্সেস ও মূল বৈশিষ্ট্যগুলির জন্য প্রয়োজনীয়",
    p1Label: "ব্যবহার বিশ্লেষণ",
    p1Desc: "অজ্ঞাত ব্যবহার ডেটা শেয়ার করে আমাদের উন্নতিতে সাহায্য করুন",
    p2Label: "মার্কেটিং যোগাযোগ",
    p2Desc: "প্রমো, আপডেট ও ব্যক্তিগতকৃত সুপারিশ পান",
    p3Label: "ব্যক্তিগতকরণ",
    p3Desc: "আপনার কার্যকলাপের ভিত্তিতে তৈরি ব্যক্তিগত অভিজ্ঞতা",
    retentionLabel: "ধারণকাল",
    retentionValue: "অ্যাকাউন্ট মুছে না দেওয়া পর্যন্ত",
    locationLabel: "ডেটার অবস্থান",
    locationValue: "মুম্বাই, ভারত (ISO 27001)",
    thirdPartyLabel: "তৃতীয় পক্ষ",
    thirdPartyValue: "শুধুমাত্র যাচাইকৃত ভেন্ডর",
    privacyNotice: "গোপনীয়তা বিজ্ঞপ্তি",
    noticeText:
      "আমরা প্ল্যাটফর্ম পরিষেবা প্রদানের জন্য আপনার ব্যক্তিগত ডেটা সংগ্রহ ও প্রক্রিয়া করি। DPDP আইন 2023 অনুযায়ী আপনার সম্মতি প্রত্যাহার, ডেটা অ্যাক্সেস, সংশোধন বা মুছে ফেলা এবং অভিযোগ দায়ের করার অধিকার রয়েছে।",
    selectLang: "ভাষা নির্বাচন করুন",
    searchPlaceholder: "ভাষা খুঁজুন...",
    required: "প্রয়োজনীয়",
    optional: "ঐচ্ছিক",
  },
  // ── Assamese (as) ────────────────────────────────────────────────────────
  as: {
    heading: "আপোনা গোপনীয়তা বাছনি",
    subheading: "DPDP আইন, 2023 অনুসৰে",
    acceptBtn: "বাছনি গ্রহণ কৰক আৰু আগবাঢ়ক",
    manageBtn: "পছন্দসমূহ পৰিচালনা কৰক",
    rejectBtn: "অ-প্রয়োজনীয় প্রত্যাখ্যান কৰক",
    acceptAllBtn: "সকলো গ্রহণ কৰক & আগবাঢ়ক",
    backBtn: "পিছে",
    cancelBtn: "বাতিল কৰক",
    customize: "কাষ্টমাইজ কৰক",
    acceptSelected: (n) => `${n}টো গ্রহণ কৰক & আগবাঢ়ক`,
    p0Label: "একাউণ্ট আৰু সেৱা প্ৰদান",
    p0Desc: "প্লেটফর্ম প্রৱেশ আৰু মূল বৈশিষ্ট্যৰ বাবে প্রয়োজনীয়",
    p1Label: "ব্যৱহাৰ বিশ্লেষণ",
    p1Desc: "অজ্ঞাত ব্যৱহাৰ তথ্য ভাগ কৰি আমাক উন্নত কৰাত সহায় কৰক",
    p2Label: "বিপণন যোগাযোগ",
    p2Desc: "প্রমো, আপডেট আৰু ব্যক্তিগতকৃত সুপারিশ পাব",
    p3Label: "ব্যক্তিগতকৰণ",
    p3Desc: "আপোনা কার্যকলাপৰ ভিত্তিত তৈরি ব্যক্তিগত অভিজ্ঞতা",
    retentionLabel: "ধারণকাল",
    retentionValue: "একাউণ্ট মচি নোযোৱা পর্যন্ত",
    locationLabel: "তথ্যৰ অৱস্থান",
    locationValue: "মুম্বাই, ভাৰত (ISO 27001)",
    thirdPartyLabel: "তৃতীয় পক্ষ",
    thirdPartyValue: "কেৱল পৰীক্ষিত বিক্রেতা",
    privacyNotice: "গোপনীয়তা বিজ্ঞপ্তি",
    noticeText:
      "আমি প্লেটফর্ম সেৱা প্ৰদানৰ বাবে আপোনাৰ ব্যক্তিগত তথ্য সংগ্রহ আৰু পৰিশোধন কৰো। DPDP আইন 2023 অনুসৰে আপোনাৰ সন্মতি প্রত্যাহাৰ, তথ্য প্রৱেশ, সংশোধন বা মচি পেলোৱা আৰু অভিযোগ দাখিল কৰাৰ অধিকাৰ আছে।",
    selectLang: "ভাষা বাছনি কৰক",
    searchPlaceholder: "ভাষা সন্ধান কৰক...",
    required: "প্রয়োজনীয়",
    optional: "ঐচ্ছিক",
  },
  // ── Bodo (brx) ──────────────────────────────────────────────────────────
  brx: {
    heading: "लानानै हरख जेमा",
    subheading: "DPDP बिल, 2023 अनुसार",
    acceptBtn: "जेमा बाहा लानानै दों",
    manageBtn: "जेमा गुदा-बार बाहा बाहैन",
    rejectBtn: "अनावश्यक बाहा नांगोन",
    acceptAllBtn: "सकल बाहा जुरुनै दों",
    backBtn: "फिरा",
    cancelBtn: "हाब्लै",
    customize: "बिलास बाहा",
    acceptSelected: (n) => `${n} बाहा जुरुनै दों`,
    p0Label: "खाता आरो सेवा दोंग’",
    p0Desc: "गोदान जायगा आरो हाननै सुबिधा खात्थि",
    p1Label: "गोदान बिलास बिगुनै",
    p1Desc: "हाननै गोदान बिलास बिगुनै मुसा बांस्लायैन",
    p2Label: "बजार हांथुनै",
    p2Desc: "हांथुनै, अपडेट आरो बिलास जायगा हांथुनै",
    p3Label: "बिलास जायगा",
    p3Desc: "आपनि गोदाना हाननै बिलास जायगा",
    retentionLabel: "सोथाय बार",
    retentionValue: "खाता हाब्लै मोनसे",
    locationLabel: "बिलास बिनन",
    locationValue: "मुम्बई, भारत (ISO 27001)",
    thirdPartyLabel: "गोथानि",
    thirdPartyValue: "बांस्लायै सुथाय बांस्लायै",
    privacyNotice: "लानानै हरख",
    noticeText:
      "गोदान जायगा सेवा दोंगु लागি आपनि बिलास बिलास जुरुनै। DPDP बिल 2023 अनुसार आपनि सोंथाय बाहा, बिलास बांस्लायै, सुथार बाहा आरो हाब्लै, आरो हाननै बार लानानै हाननै हमबु।",
    selectLang: "जुबॉन बाहा",
    searchPlaceholder: "जुबॉन खोज बाहा...",
    required: "जरगी",
    optional: "ऐच्छिक",
  },
  // ── Dogri (doi) ───────────────────────────────────────────────────────────
  doi: {
    heading: "तुम्हारी गोपनीयता चॉइस",
    subheading: "DPDP एक्ट, 2023 अंतर्गत",
    acceptBtn: "चुने हुए स्वीकार करो आरो आगे बढ़ो",
    manageBtn: "पसंद मैनेज करो",
    rejectBtn: "गैर-ज़रूरी चीज़ें रद्द करो",
    acceptAllBtn: "सभी स्वीकार करो & आगे बढ़ो",
    backBtn: "पीछे",
    cancelBtn: "रद्द करो",
    customize: "कस्टमाइज करो",
    acceptSelected: (n) => `${n} चुने स्वीकार करो`,
    p0Label: "अकाउंट आरो सेवा डिलीवरी",
    p0Desc: "प्लैटफॉर्म ऐक्सेस आरो कोर फीचर्स खातिर ज़रूरी",
    p1Label: "उपयोग ऐनलिटिक्स",
    p1Desc: "गुमनाम उपयोग डेटा शेयर कर के हमारी सुधार में मदद करो",
    p2Label: "मार्केटिंग कम्युनिकेशन",
    p2Desc: "प्रमो, अपडेट आरो पर्सनलाइज्ड सुझाव पाओ",
    p3Label: "पर्सनलाइज़ेशन",
    p3Desc: "तुम्हारी गतिविधि पर आधारित तैयार अनुभव",
    retentionLabel: "रिटेंशन पीरियड",
    retentionValue: "अकाउंट डिलीट होने तक",
    locationLabel: "डेटा लोकेशन",
    locationValue: "मुंबई, भारत (ISO 27001)",
    thirdPartyLabel: "तीसरे पक्ष",
    thirdPartyValue: "केवल वेरिफाइड वेंडर",
    privacyNotice: "प्राइवेसी नोटिस",
    noticeText:
      "हम प्लैटफॉर्म सेवाएं देने खातिर तुम्हारा पर्सनल डेटा इकट्ठा आरो प्रोसेस करदे हैं। DPDP एक्ट 2023 अंतर्गत तुम्हारा कॉन्सेंट वापस लेने, डेटा ऐक्सेस, सुधार या मिटाने, आरो शिकायत दर्ज कराने का अधिकार है।",
    selectLang: "भाषा चुनो",
    searchPlaceholder: "भाषा खोजो...",
    required: "ज़रूरी",
    optional: "आप्शनल",
  },
  // ── Gujarati (gu) ───────────────────────────────────────────────────────
  gu: {
    heading: "તમારી ગોપનીયતા પસંદગીઓ",
    subheading: "DPDP ધારો, 2023 હેઠળ",
    acceptBtn: "પસંદ કરેલા સ્વીકારો અને ચાલુ રાખો",
    manageBtn: "પસંદગીઓ સંભાળો",
    rejectBtn: "બિન-જરૂરી નામંજૂર કરો",
    acceptAllBtn: "બધા સ્વીકારો & ચાલુ રાખો",
    backBtn: "પાછા",
    cancelBtn: "રદ કરો",
    customize: "કસ્ટમાઇઝ કરો",
    acceptSelected: (n) => `${n} સ્વીકારો & ચાલુ રાખો`,
    p0Label: "એકાઉન્ટ અને સર્વિસ ડિલિવરી",
    p0Desc: "પ્લેટફોર્મ ઍક્સેસ અને મુખ્ય સુવિધાઓ માટે જરૂરી",
    p1Label: "ઉપયોગ વિશ્લેષણ",
    p1Desc: "અનામી ઉપયોગ ડેટા શેર કરીને સુધારવામાં મદદ કરો",
    p2Label: "માર્કેટિંગ સંચાર",
    p2Desc: "પ્રમો, અપડેટ અને વ્યક્તિગત ભલામણો મેળવો",
    p3Label: "વ્યક્તિગતકરણ",
    p3Desc: "તમારી પ્રવૃત્તિ પર આધારિત ટેલર્ડ અનુભવ",
    retentionLabel: "જાળવણી સમયગાળો",
    retentionValue: "એકાઉન્ટ કાઢવા સુધી",
    locationLabel: "ડેટા સ્થાન",
    locationValue: "મુંબઈ, ભારત (ISO 27001)",
    thirdPartyLabel: "તૃતીય પક્ષો",
    thirdPartyValue: "ફક્ત ચકાસેલા વિક્રેતાઓ",
    privacyNotice: "ગોપનીયતા નોટિસ",
    noticeText:
      "અમે પ્લેટફોર્મ સેવાઓ પ્રદાન કરવા માટે તમારો વ્યક્તિગત ડેટા એકત્ર કરીએ છીએ અને પ્રક્રિયા કરીએ છીએ। DPDP ધારો 2023 હેઠળ તમને સંમતિ પાછી ખેંચવા, ડેટા ઍક્સેસ, સુધારો કે કાઢી નાખવો અને ફરિયાદ નોંધાવવાનો અધિકાર છે।",
    selectLang: "ભાષા પસંદ કરો",
    searchPlaceholder: "ભાષા શોધો...",
    required: "જરૂરી",
    optional: "વૈકલ્પિક",
  },
  // ── Kannada (kn) ──────────────────────────────────────────────────────────
  kn: {
    heading: "ನಿಮ್ಮ ಗೌಪ್ಯತೆ ಆಯ್ಕೆಗಳು",
    subheading: "DPDP ಕಾಯ್ದೆ, 2023ರ ಅಡಿಯಲ್ಲಿ",
    acceptBtn: "ಆಯ್ಕೆಗಳನ್ನು ಒಪ್ಪಿಕೊಂಡು ಮುಂದುವರಿಸಿ",
    manageBtn: "ಆಯ್ಕೆಗಳನ್ನು ನಿರ್ವಹಿಸಿ",
    rejectBtn: "ಅಗತ್ಯವಲ್ಲದ್ದನ್ನು ತಿರಸ್ಕರಿಸಿ",
    acceptAllBtn: "ಎಲ್ಲಾ ಒಪ್ಪಿಕೊಳ್ಳಿ & ಮುಂದುವರಿಸಿ",
    backBtn: "ಹಿಂತಿರು",
    cancelBtn: "ರದ್ದುಗೊಳಿಸಿ",
    customize: "ಕಸ್ಟಮೈಸ್ ಮಾಡಿ",
    acceptSelected: (n) => `${n} ಆಯ್ಕೆ ಒಪ್ಪಿಕೊಳ್ಳಿ`,
    p0Label: "ಖಾತೆ ಮತ್ತು ಸೇವೆ ನೀಡಿಕೆ",
    p0Desc: "ಪ್ಲಾಟ್‌ಫಾರ್ಮ ಪ್ರವೇಶ ಮತ್ತು ಮುಖ್ಯ ವೈಶಿಷ್ಟ್ಯಗಳಿಗೆ ಅಗತ್ಯ",
    p1Label: "ಬಳಕೆ ವಿಶ್ಲೇಷಣೆ",
    p1Desc: "ಅನಾಮಿಕ ಬಳಕೆ ಡೇಟಾ ಹಂಚಿಕೊಂಡು ನಮ್ಮ ಸುಧಾರಣೆಗೆ ಸಹಾಯ ಮಾಡಿ",
    p2Label: "ಮಾರ್ಕೆಟಿಂಗ್ ಸಂಪರ್ಕಗಳು",
    p2Desc: "ಪ್ರಮೋ, ಅಪ್‌ಡೇಟ್ ಮತ್ತು ವೈಯಕ್ತಿಕಗೊಳಿಸಿದ ಶಿಫಾರಸುಗಳನ್ನು ಪಡೆಯಿರಿ",
    p3Label: "ವೈಯಕ್ತಿಕಗೊಳಿಸುವಿಕೆ",
    p3Desc: "ನಿಮ್ಮ ಚಟುವಟಿಕೆಯ ಆಧಾರದ ಮೇಲೆ ರಚಿಸಲಾದ ಅನುಭವ",
    retentionLabel: "ಧಾರಣ ಅವಧಿ",
    retentionValue: "ಖಾತೆ ಅಳಿಸುವಿಕೆಯವರೆಗೆ",
    locationLabel: "ಡೇಟಾ ಸ್ಥಳ",
    locationValue: "ಮುಂಬೈ, ಭಾರತ (ISO 27001)",
    thirdPartyLabel: "ಮೂರನೇ ಪಕ್ಷಗಳು",
    thirdPartyValue: "ಮಾತ್ರ ಪರಿಶೀಲಿಸಲಾದ ವಿಕ್ರೇತಾರಿ",
    privacyNotice: "ಗೌಪ್ಯತೆ ಸೂಚನೆ",
    noticeText:
      "ಪ್ಲಾಟ್‌ಫಾರ್ಮ ಸೇವೆಗಳನ್ನು ಒದಗಿಸಲು ನಿಮ್ಮ ವೈಯಕ್ತಿಕ ಡೇಟಾವನ್ನು ಸಂಗ್ರಹಿಸುತ್ತೇವೆ ಮತ್ತು ಪ್ರಕ್ರಿಯೆಗೊಳಿಸುತ್ತೇವೆ। ನೀವು DPDP ಕಾಯ್ದೆ 2023ರ ಅಡಿಯಲ್ಲಿ ಒಪ್ಪಿಗೆಯನ್ನು ಹಿಂತೆಗೆದುಕೊಳ್ಳಲು, ಡೇಟಾವನ್ನು ಪ್ರವೇಶಿಸಲು, ಸರಿಪಡಿಸಲು ಅಥವಾ ಅಳಿಸಲು ಮತ್ತು ದೂರು ನೀಡಲು ಹಕ್ಕ್ ಹೊಂದಿದ್ದೀರಿ।",
    selectLang: "ಭಾಷೆ ಆಯ್ಕೆಮಾಡಿ",
    searchPlaceholder: "ಭಾಷೆ ಹುಡುಕಿ...",
    required: "ಅವಶ್ಯಕ",
    optional: "ಐಚ್ಛಿಕ",
  },
  // ── Kashmiri (ks) — RTL ─────────────────────────────────────────────────
  ks: {
    heading: "व्यक्तिगत ستوری چویس",
    subheading: "DPDP ایکٹ, 2023",
    acceptBtn: "منتخبہ پسند اکنہ تہہ جاری رکھو",
    manageBtn: "پسندہ ترتیب دو",
    rejectBtn: "غیر ضروری رفض کرو",
    acceptAllBtn: "سارے پسند اکنہ تہہ جاری رکھو",
    backBtn: "وپس",
    cancelBtn: "منسوخ کرو",
    customize: "ترتیب بدلو",
    acceptSelected: (n) => `${n} منتخبہ پسند اکنہ`,
    p0Label: "کھاتہ تہہ خدمت فراہمی",
    p0Desc: "پلیٹ فارم رسائی تہہ بنیادی سہولہ خانے لئے ضروری",
    p1Label: "استعمال تجزیہ",
    p1Desc: "گمنام استعمال ڈیٹا شیئر کرکے سودھنے میں مدد کرو",
    p2Label: "مارکیٹنگ مواصلات",
    p2Desc: "پرومو، اپ ڈیٹ تہہ ذاتی تجاویز پائو",
    p3Label: "ذاتی کیہان",
    p3Desc: "تمہاری سرگرمی دی اساس تے رچیا گیا ذاتی تجربہ",
    retentionLabel: "راتب دور",
    retentionValue: "کھاتہ حذف تیکنہ",
    locationLabel: "ڈیٹا مقام",
    locationValue: "ممبئی, بھارت (ISO 27001)",
    thirdPartyLabel: "تیسری پارٹی",
    thirdPartyValue: "صرف تصدیق شدہ وینڈر",
    privacyNotice: "نجی ستوری",
    noticeText:
      "اسی پلیٹ فارم خدمتہ فراہم کرنہ لئے تمہاری ذاتی ڈیٹا اکٹھ کریہ تہہ پروسیس کریہ۔ DPDP ایکٹ 2023 دے تحت تمہارے پاس رضامندی واپس لینہ، ڈیٹا رسائی، درستگی یا حذف، تہہ شکایت درج کرانہ کا حق اے۔",
    selectLang: "زبان منتخب کرو",
    searchPlaceholder: "زبان ڈھونڈو...",
    required: "ضروری",
    optional: "اختیاری",
  },
  // ── Konkani (kok) ───────────────────────────────────────────────────────
  kok: {
    heading: "तुमची गुप्तता निवड",
    subheading: "DPDP कायदो, 2023 अंतर्गत",
    acceptBtn: "निवडल्ले स्वीकार करचें आनी पुढार चालू करचें",
    manageBtn: "निवडी मॅनेज करच्यात",
    rejectBtn: "अनावश्यक नाकार्च्यात",
    acceptAllBtn: "सकल स्वीकार करचें & पुढार चालू करचें",
    backBtn: "मागे",
    cancelBtn: "रद्द करचें",
    customize: "कस्टमाइज करचें",
    acceptSelected: (n) => `${n} निवडल्ले स्वीकार करचें`,
    p0Label: "खातें आनी सेवा दिंवप",
    p0Desc: "प्लॅटफॉर्म प्रवेश आनी मुखेल सोयां खातीर जायराव",
    p1Label: "वापर विश्लेषण",
    p1Desc: "अज्ञात वापर माहिती वांटून आमकां सुधार्पा मदत करचें",
    p2Label: "मार्केटिंग संवाद",
    p2Desc: "प्रमो, अपडेट आनी वैयक्तिक सूचना मेळयात",
    p3Label: "वैयक्तिकरण",
    p3Desc: "तुम्या कृतीच्या आधारान तयार केल्लो अनुभव",
    retentionLabel: "जतन काळ",
    retentionValue: "खातें मेटयनांपर्यंत",
    locationLabel: "माहिती जागो",
    locationValue: "मुंबई, भारत (ISO 27001)",
    thirdPartyLabel: "तिसरो पक्ष",
    thirdPartyValue: "फक्त तपासल्ले विक्रेते",
    privacyNotice: "गुप्तता सूचना",
    noticeText:
      "आय प्लॅटफॉर्म सेवा दिंवपाखातीर तुमची वैयक्तिक माहिती एकठांय करता आनी प्रक्रिया करता। DPDP कायदो 2023 अंतर्गत तुमकां संमती मागे घेवप, माहिती प्रवेश, दुरुस्ती या मेटप, आनी तक्रार नोंदावपाचो अधिकार आसा।",
    selectLang: "भास निवडयात",
    searchPlaceholder: "भास शोधयात...",
    required: "जायराव",
    optional: "आप्शनल",
  },
  // ── Maithili (mai) ──────────────────────────────────────────────────────
  mai: {
    heading: "अहाँक भेंसि पसनि चयन",
    subheading: "DPDP अधिनियम, 2023 क अंतर्गत",
    acceptBtn: "चयन केल&nbsp;स्वीकार क&nbsp;आ द&nbsp;जारी राखू",
    manageBtn: "पसनि प्रबंधित करू",
    rejectBtn: "गैर-आवश्यक अस्वीकृत करू",
    acceptAllBtn: "सभी स्वीकार करू & जारी राखू",
    backBtn: "पीछा",
    cancelBtn: "रद्द करू",
    customize: "अनुकूलित करू",
    acceptSelected: (n) => `${n} चयन स्वीकार करू`,
    p0Label: "खाता आ&nbsp;सेवा वितरण",
    p0Desc: "मंच प्रवेश आ&nbsp;मुख्य सुविधाक लेल आवश्यक",
    p1Label: "उपयोग विश्लेषण",
    p1Desc: "गुमनाम उपयोग डेटा बांटि हमर सुधारमे मदद करू",
    p2Label: "विपणन संचार",
    p2Desc: "प्रमो, अपडेट आ&nbsp;व्यक्तिगत सुझाव प्राप्त करू",
    p3Label: "व्यक्तिगतकरण",
    p3Desc: "अहाँक गतिविधिक आधारित तैयार अनुभव",
    retentionLabel: "प्रतिधारण अवधि",
    retentionValue: "खाता विलोपन धरि",
    locationLabel: "डेटा स्थान",
    locationValue: "मुंबई, भारत (ISO 27001)",
    thirdPartyLabel: "तृतीय पक्ष",
    thirdPartyValue: "केवल सत्यापित विक्रेता",
    privacyNotice: "भेंसि सूचना",
    noticeText:
      "हम प्लेटफॉर्म सेवा प्रदान करबा लेल अहाँक व्यक्तिगत डेटा संग्रह आ&nbsp;प्रसंस्करण करैत छी। DPDP अधिनियम 2023 क अंतर्गत अहाँक सेहमति वापस लेवाक, डेटा प्रवेश, सुधार या विलोपन, आ&nbsp;शिकायत दर्ज करबाक अधिकार अछि।",
    selectLang: "भाषा चयन करू",
    searchPlaceholder: "भाषा खोजू...",
    required: "आवश्यक",
    optional: "वैकल्पिक",
  },
  // ── Malayalam (ml) ─────────────────────────────────────────────────────
  ml: {
    heading: "നിങ്ങളുടെ സ്വകാര്യത തിരഞ്ഞെടുപ്പുകൾ",
    subheading: "DPDP ആക്ട്, 2023 പ്രകാരം",
    acceptBtn: "തിരഞ്ഞെടുത്തവ സ്വീകരിച്ച് തുടരുക",
    manageBtn: "മുൻഗണനകൾ കൈകാര്യം ചെയ്യുക",
    rejectBtn: "ആവശ്യമില്ലാത്തവ നിരസിക്കുക",
    acceptAllBtn: "എല്ലാം സ്വീകരിക്കുക & തുടരുക",
    backBtn: "തിരികെ",
    cancelBtn: "റദ്ദാക്കുക",
    customize: "ഇഷ്ടാനുസൃതമാക്കുക",
    acceptSelected: (n) => `${n} സ്വീകരിക്കുക`,
    p0Label: "അക്കൗണ്ടും സേവന വിതരണവും",
    p0Desc: "പ്ലാറ്റ്‌ഫോം ആക്‌സസും പ്രധാന സവിശേഷതകൾക്കും അത്യാവശ്യം",
    p1Label: "ഉപയോഗ വിശകലനം",
    p1Desc: "അജ്ഞാത ഉപയോഗ ഡാറ്റ പങ്കിട്ട് ഞങ്ങളെ മെച്ചപ്പെടുത്താൻ സഹായിക്കുക",
    p2Label: "മാർക്കറ്റിംഗ് ആശയവിനിമയം",
    p2Desc: "പ്രമോ, അപ്‌ഡേറ്റുകളും വ്യക്തിഗതഗൊളിച്ച ശുപാർശകൾ നേടുക",
    p3Label: "വ്യക്തിഗതഗൊളിച്ചകരണം",
    p3Desc: "നിങ്ങളുടെ പ്രവൃത്തിയെ അടിസ്ഥാനമാക്കിയ ഇഷ്ടാനുസൃത അനുഭവം",
    retentionLabel: "സംരക്ഷണ കാലയളവ്",
    retentionValue: "അക്കൗണ്ട് ഇല്ലാതാക്കുന്നത് വരെ",
    locationLabel: "ഡാറ്റ സ്ഥാനം",
    locationValue: "മുംബൈ, ഇന്ത്യ (ISO 27001)",
    thirdPartyLabel: "മൂന്നാം കക്ഷികൾ",
    thirdPartyValue: "സാധൃത വിതരണക്കാരൻ മാത്രം",
    privacyNotice: "സ്വകാര്യത അറിയിപ്പ്",
    noticeText:
      "പ്ലാറ്റ്‌ഫോം സേവനങ്ങൾ നൽകുന്നതിനായി നിങ്ങളുടെ വ്യക്തിഗത ഡാറ്റ ശേഖരിക്കുകയും പ്രക്രിയചെയ്യുകയും ചെയ്യുന്നു. നിങ്ങൾക്ക് DPDP ആക്ട് 2023 പ്രകാരം സമ്മതം പിൻവലിക്കാനും, ഡാറ്റ ആക്‌സസ്, തിരുത്തലോ ഇല്ലാതാക്കലോ ചെയ്യാനും, പരാതി നൽകാനും അവകാശമുണ്ട്।",
    selectLang: "ഭാഷ തിരഞ്ഞെടുക്കുക",
    searchPlaceholder: "ഭാഷ തിരഞ്ഞെടുക്കുക...",
    required: "ആവശ്യം",
    optional: "ഐച്ഛികം",
  },
  // ── Manipuri / Meitei (mni) ──────────────────────────────────────────────
  mni: {
    heading: "ꯑꯣꯏꯁꯨꯝꯖꯤ ꯃꯇꯦ",
    subheading: "DPDP ꯑꯦꯛꯠ, 2023",
    acceptBtn: "ꯆꯠꯟꯕꯤ ꯁ꯭ꯋꯥꯡꯂꯨ ꯫",
    manageBtn: "ꯃꯇꯦ ꯂꯣꯏꯁ꯭ꯗꯤ ꯫",
    rejectBtn: "ꯑꯆꯧꯕ ꯁ꯭ꯋꯥꯡꯂꯨ",
    acceptAllBtn: "ꯊꧤ ꯁ꯭ꯋꯥꯡꯂꯨ",
    backBtn: "ꯁ꯭ꯛꯁꯤ",
    cancelBtn: "ꯔꯦꯖꯤꯜ ꯫",
    customize: "ꯃꯇꯦ ꯊꯣꯏꯔꯤ",
    acceptSelected: (n) => `${n} ꯁ꯭ꯋꯥꯡꯂꯨ`,
    p0Label: "ꯗꯤꯐꯥꯜ ꯫",
    p0Desc: "ꯄ꯭ꯂꯦꯠꯐ꣝ꯕꯔ ꯑꯀꯁꯤꯡ ꯫",
    p1Label: "ꯑꯣꯏꯁꯨꯝꯖꯤ ꯁ꯭ꯛꯁꯤꯟ",
    p1Desc: "ꯁ꯭ꯛꯁꯤꯡ ꯂꯣꯏꯁ꯭ꯗꯤ ꯫",
    p2Label: "ꯃꯥꯔꯀꯠꯤꯝ ꯁ꯭ꯛꯄꯣꯠ",
    p2Desc: "ꯄ꯭ꯔ꣜ꯃ꣝ ꯫",
    p3Label: "ꯃꯇꯦ ꯃꯈꯜ ꯫",
    p3Desc: "ꯑꯣꯏꯁꯨꯝꯖꯤ ꯒꯤ ꯁꯛꯄꯣꯠ",
    retentionLabel: "ꯐꯣꯏꯁꯤꯡ",
    retentionValue: "ꯗꯤꯐꯥꯜ ꯆꯠꯂꯤ",
    locationLabel: "ꯁ꯭ꯛꯁꯤꯡ",
    locationValue: "ꯃꯨꯝꯕꯦ, ꯄ꯭ꯔꯥꯠ (ISO 27001)",
    thirdPartyLabel: "ꯊꯤꯡꯆꯤ",
    thirdPartyValue: "ꯁ꯭ꯛꯁꯤꯡ",
    privacyNotice: "ꯑꯣꯏꯁꯨꯝꯖꯤ ꯫",
    noticeText:
      "ꯄ꯭ꯂꯦꯠꯐ꣝ꯕꯔ ꯁ꯭ꯚꯤꯁ ꯆꯠꯕꯤ ꯑꯣꯏꯁꯨꯝꯖꯤ ꯁ꯭ꯛꯄꯣꯠ ꯂꯧꯕꯨ ꯫",
    selectLang: "ꯆꯤꯡ ꯂꯣꯏꯁ꯭ꯗꯤ",
    searchPlaceholder: "ꯆꯤꯡ ꯍꯧꯔꯤ...",
    required: "ꯃꯇꯦ",
    optional: "ꯑꯀꯛꯂꯣꯏ꯫",
  },
  // ── Nepali (ne) ────────────────────────────────────────────────────────
  ne: {
    heading: "तपाईंको गोपनीयता छनौट",
    subheading: "DPDP ऐन, 2023 अनुसार",
    acceptBtn: "छनौट स्वीकार गर्नुहोस् र जारी राख्नुहोस्",
    manageBtn: "प्राथमिकताहरू व्यवस्थापन गर्नुहोस्",
    rejectBtn: "गैर-आवश्यक अस्वीकार गर्नुहोस्",
    acceptAllBtn: "सबै स्वीकार गर्नुहोस् & जारी राख्नुहोस्",
    backBtn: "पछाडि",
    cancelBtn: "रद्द गर्नुहोस्",
    customize: "अनुकूलन गर्नुहोस्",
    acceptSelected: (n) => `${n} स्वीकार गर्नुहोस्`,
    p0Label: "खाता र सेवा वितरण",
    p0Desc: "प्लेटफर्म पहुँच र मुख्य सुविधाहरूको लागि आवश्यक",
    p1Label: "प्रयोग विश्लेषण",
    p1Desc: "गुमनाम प्रयोग डेटा साझा गरेर हामीलाई सुधार्न मदद गर्नुहोस्",
    p2Label: "मार्केटिङ संचार",
    p2Desc: "प्रमो, अपडेट र व्यक्तिगत सुझावहरू प्राप्त गर्नुहोस्",
    p3Label: "व्यक्तिगतकरण",
    p3Desc: "तपाईंको गतिविधिको आधारमा तयार गरिएको अनुभव",
    retentionLabel: "प्रतिधारण अवधि",
    retentionValue: "खाता हटाउनेसम्म",
    locationLabel: "डेटा स्थान",
    locationValue: "मुंबई, भारत (ISO 27001)",
    thirdPartyLabel: "तेस्रो पक्षहरू",
    thirdPartyValue: "केवल प्रमाणित विक्रेता",
    privacyNotice: "गोपनीयता सूचना",
    noticeText:
      "हामी प्लेटफर्म सेवाहरू प्रदान गर्न तपाईंको व्यक्तिगत डेटा सङ्कलन र प्रशोधन गर्छौं। तपाईंलाई DPDP ऐन 2023 अनुसार सहमति फिर्ता लिने, डेटा पहुँच, सुधार वा मेटाउने र गुनासो दर्ता गर्ने अधिकार छ।",
    selectLang: "भाषा चयन गर्नुहोस्",
    searchPlaceholder: "भाषा खोज्नुहोस्...",
    required: "आवश्यक",
    optional: "वैकल्पिक",
  },
  // ── Odia (or) ──────────────────────────────────────────────────────────
  or: {
    heading: "ଆପଣାର ଗୋପନୀୟ ପସନ୍ଦ",
    subheading: "DPDP ଅଧିନିୟମ, 2023 ଅନୁସାରେ",
    acceptBtn: "ଚୟନ କରନ୍ତୁ ଏବଂ ଆଗେ ବଢିନ୍ତୁ",
    manageBtn: "ପସନ୍ଦ ପରିଚାଳନା କରନ୍ତୁ",
    rejectBtn: "ଅ-ଆବଶ୍ୟକ ପ୍ରତ୍ୟାଖ୍ୟନ କରନ୍ତୁ",
    acceptAllBtn: "ସବୁ ଗ୍ରହଣ କରନ୍ତୁ & ଆଗେ ବଢିନ୍ତୁ",
    backBtn: "ପଛକୁ",
    cancelBtn: "ବାତିଲ କରନ୍ତୁ",
    customize: "କଷ୍ଟମାଇଜ କରନ୍ତୁ",
    acceptSelected: (n) => `${n} ଗ୍ରହଣ କରନ୍ତୁ`,
    p0Label: "ଖାତା ଏବଂ ସେବା ବିତରଣ",
    p0Desc: "ପ୍ଲାଟଫର୍ମ ପ୍ରବେଶ ଏବଂ ମୂଳ ବୈଶିଷ୍ଟ୍ୟ ପାଇଁ ଆବଶ୍ୟକ",
    p1Label: "ବ୍ୟବହାର ବିଶ୍ଳେଷଣ",
    p1Desc: "ଅଜ୍ଞାତ ବ୍ୟବହାର ତଥ୍ୟ ଶେୟର କରି ଆମକୁ ଉନ୍ନତି କରିବାରେ ସାହାୟ କରନ୍ତୁ",
    p2Label: "ମାର୍କେଟିଂ ଯୋଗାଯୋଗ",
    p2Desc: "ପ୍ରୋମୋ, ଅପଡେଟ ଏବଂ ବ୍ୟକ୍ତିଗତ ସୁପାରିଶ ପାଇଁ",
    p3Label: "ବ୍ୟକ୍ତିଗତକରଣ",
    p3Desc: "ଆପଣାର କାର୍ଯ୍ୟକଳାପ ଉପରେ ଆଧାରିତ ତିଆରା ଅନୁଭବ",
    retentionLabel: "ଧାରଣ ସମୟ",
    retentionValue: "ଖାତା ବିଲୋପ ନ ହେବା ପର୍ଯ୍ୟନ୍ତ",
    locationLabel: "ତଥ୍ୟ ଅବସ୍ଥାନ",
    locationValue: "ମୁଂବଈ, ଭାରତ (ISO 27001)",
    thirdPartyLabel: "ତୃତୀୟ ପକ୍ଷ",
    thirdPartyValue: "କେବଳ ଯାଚା ହୋଇଥିବା ବିକ୍ରେତା",
    privacyNotice: "ଗୋପନୀୟ ସୂଚନା",
    noticeText:
      "ଆମେ ପ୍ଲାଟଫର୍ମ ସେବା ପ୍ରଦାନ କରିବା ପାଇଁ ଆପଣାର ବ୍ୟକ୍ତିଗତ ତଥ୍ୟ ସଂଗ୍ରହ ଏବଂ ପ୍ରକ୍ରିୟା କରୁ। DPDP ଅଧିନିୟମ 2023 ଅନୁସାରେ ଆପଣାକୁ ସମ୍ମତି ଫେରାଇବା, ତଥ୍ୟ ପ୍ରବେଶ, ସଂଶୋଧନ କିମ୍ବା ଅଳିସିବା ଏବଂ ଅଭୟୋଗ ଦାଖଲ କରିବାର ଅଧିକାର ଅଛି।",
    selectLang: "ଭାଷା ଚୟନ କରନ୍ତୁ",
    searchPlaceholder: "ଭାଷା ଖୋଜନ୍ତୁ...",
    required: "ଆବଶ୍ୟକ",
    optional: "ଐଚ୍ଛିକ",
  },
  // ── Punjabi (pa) ────────────────────────────────────────────────────────
  pa: {
    heading: "ਤੁਹਾਡੀ ਪਰਦੇਦਾਰੀ ਚੋਣਾਂ",
    subheading: "DPDP ਐਕਟ, 2023 ਦੇ ਤਹਿਤ",
    acceptBtn: "ਚੁਣੇ ਹੋਏ ਸਵੀਕਾਰ ਕਰੋ ਅਤੇ ਜਾਰੀ ਰੱਖੋ",
    manageBtn: "ਤਰਜੀਹਾਂ ਪ੍ਰਬੰਧਿਤ ਕਰੋ",
    rejectBtn: "ਗੈਰ-ਜ਼ਰੂਰੀ ਰੱਦ ਕਰੋ",
    acceptAllBtn: "ਸਾਰੇ ਸਵੀਕਾਰ ਕਰੋ & ਜਾਰੀ ਰੱਖੋ",
    backBtn: "ਵਾਪਸ",
    cancelBtn: "ਰੱਦ ਕਰੋ",
    customize: "ਕਸਟਮਾਈਜ਼ ਕਰੋ",
    acceptSelected: (n) => `${n} ਸਵੀਕਾਰ ਕਰੋ`,
    p0Label: "ਅਕਾਊਂਟ ਅਤੇ ਸੇਵਾ ਡਿਲੀਵਰੀ",
    p0Desc: "ਪਲੇਟਫਾਰਮ ਪਹੁੰਚ ਅਤੇ ਮੁੱਖ ਵਿਸ਼ੇਸ਼ਤਾਵਾਂ ਲਈ ਜ਼ਰੂਰੀ",
    p1Label: "ਵਰਤੋਂ ਵਿਸ਼ਲੇਸ਼ਣ",
    p1Desc: "ਗੁੰਮ ਨਾਂ ਵਰਤੋਂ ਡੇਟਾ ਸ਼ੇਅਰ ਕਰਕੇ ਸਾਨੂੰ ਸੁਧਾਰਨ ਵਿੱਚ ਮਦਦ ਕਰੋ",
    p2Label: "ਮਾਰਕੀਟਿੰਗ ਸੰਪਰਕ",
    p2Desc: "ਪ੍ਰੋਮੋ, ਅੱਪਡੇਟ ਅਤੇ ਨਿੱਜੀ ਸੁਝਾਅ ਪ੍ਰਾਪਤ ਕਰੋ",
    p3Label: "ਨਿੱਜੀਕਰਣ",
    p3Desc: "ਤੁਹਾਡੀ ਗਤੀਵਿਧੀ 'ਤੇ ਅਧਾਰਤ ਤਿਆਰ ਅਨੁਭਵ",
    retentionLabel: "ਰਿਟੈਂਸ਼ਨ ਮਿਆਦ",
    retentionValue: "ਅਕਾਊਂਟ ਹਟਾਉਣ ਤੱਕ",
    locationLabel: "ਡੇਟਾ ਟਿਕਾਣਾ",
    locationValue: "ਮੁੰਬਈ, ਭਾਰਤ (ISO 27001)",
    thirdPartyLabel: "ਤੀਜੇ ਧਿਰ",
    thirdPartyValue: "ਸਿਰਫ਼ ਪ੍ਰਮਾਣਿਤ ਵਿਕਰੇਤਾ",
    privacyNotice: "ਪਰਦੇਦਾਰੀ ਸੂਚਨਾ",
    noticeText:
      "ਅਸੀਂ ਪਲੇਟਫਾਰਮ ਸੇਵਾਵਾਂ ਪ੍ਰਦਾਨ ਕਰਨ ਲਈ ਤੁਹਾਡਾ ਨਿੱਜੀ ਡੇਟਾ ਇਕੱਠਾ ਅਤੇ ਪ੍ਰਕ੍ਰਿਆ ਕਰਦੇ ਹਾਂ। DPDP ਐਕਟ 2023 ਦੇ ਤਹਿਤ ਤੁਹਾਡਾ ਸੰਮ੍ਰੱਤੀ ਵਾਪਸ ਲੈਣ, ਡੇਟਾ ਪਹੁੰਚ, ਸੋਧ ਜਾਂ ਮਿਟਾਉਣ ਅਤੇ ਸ਼ਿਕਾਇਤ ਦਰਜ ਕਰਾਉਣ ਦਾ ਅਧਿਕਾਰ ਹੈ।",
    selectLang: "ਭਾਸ਼ਾ ਚੁਣੋ",
    searchPlaceholder: "ਭਾਸ਼ਾ ਲੱਭੋ...",
    required: "ਜ਼ਰੂਰੀ",
    optional: "ਵਿਕਲਪਕ",
  },
  // ── Sanskrit (san) ───────────────────────────────────────────────────────
  san: {
    heading: "भवतः गोपनीयता विकल्पाः",
    subheading: "DPDP अधिनियमेन 2023 सह",
    acceptBtn: "चितानि स्वीकृत्य प्रचलामः",
    manageBtn: "विकल्पान् प्रबन्धयामः",
    rejectBtn: "अनावश्यकानि निराकरोमः",
    acceptAllBtn: "सर्वाणि स्वीकृत्य प्रचलामः",
    backBtn: "पूर्वं गच्छामः",
    cancelBtn: "निरस्यामः",
    customize: "रूपयामः",
    acceptSelected: (n) => `${n} चितानि स्वीकुर्वन्तु`,
    p0Label: "पणः खातिं च सेवा च",
    p0Desc: "मञ्चप्रवेशाय मुख्यसुविधाभ्यः आवश्यकम्",
    p1Label: "उपयोगविश्लेषणम्",
    p1Desc: "अज्ञातोपयोगदत्तांशं विभज्य सन्मार्गयामः",
    p2Label: "वाणिज्यसंचारः",
    p2Desc: "प्रोमो, अद्यतनं व्यक्तिगतं च सुझावं प्राप्नुमः",
    p3Label: "व्यक्तिगतकर्म",
    p3Desc: "भवतः क्रियायां आधारिता तनुजा अनुभवः",
    retentionLabel: "निगरणकालः",
    retentionValue: "पणः नाशनपर्यन्तम्",
    locationLabel: "दत्तांशस्थानम्",
    locationValue: "मुम्बई, भारतम् (ISO 27001)",
    thirdPartyLabel: "तृतीयपक्षः",
    thirdPartyValue: "केवलं परीक्षितविक्रेता",
    privacyNotice: "गोपनीयतासूचना",
    noticeText:
      "वयं मञ्चसेवाभ्यः पणः व्यक्तिकदत्तांशं संगृह्णीमहे प्रक्रियामश्च कुर्महे। DPDP अधिनियमेन 2023 सह पणः सहमतिं प्रतिदातुं, दत्तांशं प्राप्तुं, संशोधयितुं वा अपनयितुं शिकायतं च दातुमधिकारी अस्ति।",
    selectLang: "भाषां वृणुताम्",
    searchPlaceholder: "भाषां अन्वेषताम्...",
    required: "आवश्यकम्",
    optional: "विकल्पात्मकम्",
  },
  // ── Santali (sat) ────────────────────────────────────────────────────────
  sat: {
    heading: "Onko horo luron selection",
    subheading: "DPDP Act, 2023 reak",
    acceptBtn: "Selection banayea horop otrag bidi",
    manageBtn: "Horop manage kana",
    rejectBtn: "Onko reaigal reject kana",
    acceptAllBtn: "Horo horop otrag bidi",
    backBtn: "Pichra",
    cancelBtn: "Cancel kana",
    customize: "Customize kana",
    acceptSelected: (n) => `${n} horop otrag bidi`,
    p0Label: "Account sar gain service delivery",
    p0Desc: "Platform access gain core features reak jem",
    p1Label: "Usage analytics",
    p1Desc: "Anonymised usage data share kana surum kana",
    p2Label: "Marketing communication",
    p2Desc: "Promos, updates gain personalise surum kana",
    p3Label: "Personalisation",
    p3Desc: "Onko horo activity basi experience",
    retentionLabel: "Retention period",
    retentionValue: "Account delete hopon",
    locationLabel: "Data location",
    locationValue: "Mumbai, India (ISO 27001)",
    thirdPartyLabel: "Third party",
    thirdPartyValue: "Khubko vetted vendor",
    privacyNotice: "Privacy notice",
    noticeText:
      "Platform service deluna onko personal data e collection ra regression kana. DPDP Act 2023 reak onko horo consent withdraw kana, data access, correction hopon, erasure kana, gain complaint register kana hak ila",
    selectLang: "Language select kana",
    searchPlaceholder: "Language search kana...",
    required: "Required",
    optional: "Optional",
  },
  // ── Sindhi (sd) — RTL ────────────────────────────────────────────────────
  sd: {
    heading: "توهانجو پرائيويسي چونيس",
    subheading: "DPDP ایکٹ, 2023",
    acceptBtn: "چونيس ڪرلو آهي ۽ اڳتي هلو",
    manageBtn: "ترجيحون جو انتظام ڪريو",
    rejectBtn: "غير ضروري رد ڪريو",
    acceptAllBtn: "سڀ چونيس ڪرلو & اڳتي هلو",
    backBtn: "پوئين",
    cancelBtn: "لڇ ڪريو",
    customize: "ترتيب ڏيو",
    acceptSelected: (n) => `${n} چونيس ڪرلو`,
    p0Label: "اڪائونٽ ۽ خدمت ڏنو وڃي",
    p0Desc: "پلیٽ فارم رسائي ۽ بنيادي خاصيتون لاءِ ضروري",
    p1Label: "usage تجزيو",
    p1Desc: "گمنام استعمال ڊيٽا شيئر ڪريو اسان کي بهتر ڪرڻ ۾ مدد ڪريو",
    p2Label: "مارڪيٽنگ رابطو",
    p2Desc: "پرومو، اپ ڊيٽ ۽ ذاتي سنيبيهون وٺو",
    p3Label: "ذاتيڪارڻ",
    p3Desc: "توهانجي سرگرميءَ جي بنياد تي ٺاهيل تجربو",
    retentionLabel: "راتب جو دور",
    retentionValue: "اڪائونٽ ڊئليٽ ٿيڻ تائين",
    locationLabel: "ڊيٽا جو مقام",
    locationValue: "ممبئي, هندوستان (ISO 27001)",
    thirdPartyLabel: "ٽين پارٽي",
    thirdPartyValue: "صرف تصديق ٿيل وينڊرز",
    privacyNotice: "پرائيويسي نوٽس",
    noticeText:
      "اسان پلیٽ فارم خدمتون فراهم ڪرڻ لاءِ توهانجو ذاتي ڊيٽا گڏ ۽ پروسیس ڪريون ٿا. DPDP ایکٹ 2023 تحت توهانجي کي رضامندي واپس وٺڻ، ڊيٽا تائين رسائي، صحيح يا ختم ڪرڻ، ۽ شڪايت درج ڪرائڻ جو حق آهي.",
    selectLang: "ٻولي چونيس ڪريو",
    searchPlaceholder: "ٻولي ڳوليو...",
    required: "ضروري",
    optional: "آپشنل",
  },
  // ── Telugu (te) ──────────────────────────────────────────────────────────
  te: {
    heading: "మీ గోప్యతా ఎంపికలు",
    subheading: "DPDP చట్టం, 2023 ప్రకారం",
    acceptBtn: "ఎంపికలను అంగీకరించండి & కొనసాగించండి",
    manageBtn: "అభీష్టాలను నిర్వహించండి",
    rejectBtn: "అవసరములేనివి తిరస్కరించండి",
    acceptAllBtn: "అన్నీ అంగీకరించండి & కొనసాగించండి",
    backBtn: "వెనుకకు",
    cancelBtn: "రద్దు చేయండి",
    customize: "కస్టమైజ్ చేయండి",
    acceptSelected: (n) => `${n} అంగీకరించండి`,
    p0Label: "ఖాతా & సేవ అందజేత",
    p0Desc: "వేదిక ప్రవేశం & ప్రధాన లక్షణాలకు అవసరం",
    p1Label: "ఉపయోగ విశ్లేషణ",
    p1Desc: "అనామక ఉపయోగ డేటా భాగస్వామ్యం చేయడం వల్ల మమ్మల్ని మెరుగు పరచడంలో సహాయపడండి",
    p2Label: "మార్కెటింగ్ సంభాషణలు",
    p2Desc: "ప్రమోలు, అప్‌డేట్‌లు & వ్యక్తిగత సిఫార్సులు పొందండి",
    p3Label: "వ్యక్తిగతీకరణ",
    p3Desc: "మీ కార్యకలాపంపై ఆధారపడి రూపొందించబడిన అనుభవం",
    retentionLabel: "నిలుపుదల కాలం",
    retentionValue: "ఖాతా తొలగింపు వరకు",
    locationLabel: "డేటా ప్రాంతం",
    locationValue: "ముంబై, భారతదేశం (ISO 27001)",
    thirdPartyLabel: "మూడవ పక్షాలు",
    thirdPartyValue: "తనిఖీ చేయబడిన విక్రేతలు మాత్రమే",
    privacyNotice: "గోప్యత నోటీసు",
    noticeText:
      "మేము వేదిక సేవలను అందించడానికి మీ వ్యక్తిగత డేటాను సేకరించి ప్రాసెస్ చేస్తాము. DPDP చట్టం 2023 ప్రకారం మీకు సమ్మతిని ఉపసంహరించుకోవడానికి, డేటాకు ప్రవేశం, సవరణ లేదా తొలగింపు, మరియు ఫిర్యాదు నమోదు చేయడానికి హక్కు ఉంది.",
    selectLang: "భాషను ఎంచుకోండి",
    searchPlaceholder: "భాషను వెతుకు...",
    required: "అవసరం",
    optional: "ఐచ్ఛికం",
  },
  // ── Urdu (ur) — RTL ───────────────────────────────────────────────────────
  ur: {
    heading: "آپ کی رازداری کے انتخاب",
    subheading: "DPDP ایکٹ, 2023 کے تحت",
    acceptBtn: "منتخبہ قبول کریں اور جاری رکھیں",
    manageBtn: "ترجیحات کا انتظام کریں",
    rejectBtn: "غیر ضروری مسترد کریں",
    acceptAllBtn: "سب کو قبول کریں & جاری رکھیں",
    backBtn: "واپس",
    cancelBtn: "منسوخ کریں",
    customize: "اپنی مرضی سے ترتیب دیں",
    acceptSelected: (n) => `${n} منتخبہ قبول کریں`,
    p0Label: "اکاؤنٹ اور سروس ڈیلیوری",
    p0Desc: "پلیٹ فارم رسائی اور بنیادی خصوصیات کے لیے ضروری",
    p1Label: "استعمال کا تجزیہ",
    p1Desc: "گمنام استعمال کا ڈیٹا شیئر کرکے ہمیں بہتر بنانے میں مدد کریں",
    p2Label: "مارکیٹنگ مواصلات",
    p2Desc: "پروموز، اپ ڈیٹس اور ذاتی نوعیت کی سفارشات حاصل کریں",
    p3Label: "ذاتی کاری",
    p3Desc: "آپ کی سرگرمی کی بنیاد پر تیار کردہ تجربہ",
    retentionLabel: "رکھنے کی مدت",
    retentionValue: "اکاؤنٹ حذف ہونے تک",
    locationLabel: "ڈیٹا کا مقام",
    locationValue: "ممبئی, بھارت (ISO 27001)",
    thirdPartyLabel: "تیسرے فریق",
    thirdPartyValue: "صرف تصدیق شدہ وینڈرز",
    privacyNotice: "رازداری کا نوٹس",
    noticeText:
      "ہم پلیٹ فارم خدمات فراہم کرنے کے لیے آپ کا ذاتی ڈیٹا جمع اور پروسیس کرتے ہیں۔ DPDP ایکٹ 2023 کے تحت آپ کو رضامندی واپس لینے، ڈیٹا تک رسائی، تصحیح یا حذف، اور شکایت درج کرانے کا حق حاصل ہے۔",
    selectLang: "زبان منتخب کریں",
    searchPlaceholder: "زبان تلاش کریں...",
    required: "ضروری",
    optional: "اختیاری",
  },
  // ── Chhattisgarhi (hne) ─────────────────────────────────────────────────
  hne: {
    heading: "राउर गोपनीयता विकल्प",
    subheading: "DPDP एक्ट, 2023 के अनुसार",
    acceptBtn: "चुनल हुए मानों आर रउर जारी रखव",
    manageBtn: "विकल्प सँभालव",
    rejectBtn: "गैर-जरूरी चीज़ें अस्वीकार करव",
    acceptAllBtn: "सब स्वीकार करव & जारी रखव",
    backBtn: "पीछा",
    cancelBtn: "रद्द करव",
    customize: "बनावई बदलव",
    acceptSelected: (n) => `${n} स्वीकार करव & जारी रखव`,
    p0Label: "खाता आर सेवा डिलीवरी",
    p0Desc: "प्लेटफॉर्म पहुँच आर मुख्य सुविधाओं खातिर जरूरी",
    p1Label: "उपयोग विश्लेषण",
    p1Desc: "गुमनाम उपयोग डेटा बांट के हमर सुधार में मदद करव",
    p2Label: "मार्केटिंग संचार",
    p2Desc: "प्रमो, अपडेट आर व्यक्तिगत सुझाव पावव",
    p3Label: "व्यक्तिगतकरण",
    p3Desc: "राउर गतिविधि पर आधारित अनुकूलित अनुभव",
    retentionLabel: "प्रतिधारण अवधि",
    retentionValue: "खाता हटावे ले तक",
    locationLabel: "डेटा स्थान",
    locationValue: "मुंबई, भारत (ISO 27001)",
    thirdPartyLabel: "तीसरा पक्ष",
    thirdPartyValue: "केवल सत्यापित विक्रेता",
    privacyNotice: "गोपनीयता सूचना",
    noticeText:
      "हम प्लेटफॉर्म सेवाएं प्रदान करबे खातिर राउर व्यक्तिगत डेटा इकट्ठा आर प्रोसेस करेनी। DPDP एक्ट 2023 के अनुसार राउर के सहमति वापस लेवे, डेटा पहुँच, सुधार या हटावे, आर शिकायत दर्ज करावे के अधिकार है।",
    selectLang: "भाषा चुनव",
    searchPlaceholder: "भाषा खोजव...",
    required: "जरूरी",
    optional: "वैकल्पिक",
  },
};

// ---------------------------------------------------------------------------
// Language definitions — all 22 scheduled + English
// ---------------------------------------------------------------------------

const ALL_LANGS = [
  { code: "en",    native: "English",      romanised: "English",      flag: "🇬🇧" },
  { code: "hi",    native: "हिन्दी",        romanised: "Hindi",         flag: "🇮🇳" },
  { code: "ta",    native: "தமிழ்",          romanised: "Tamil",         flag: "🇮🇳" },
  { code: "mr",    native: "मराठी",         romanised: "Marathi",        flag: "🇮🇳" },
  { code: "bn",    native: "বাংলা",          romanised: "Bengali",        flag: "🇮🇳" },
  { code: "te",    native: "తెలుగు",          romanised: "Telugu",         flag: "🇮🇳" },
  { code: "kn",    native: "ಕನ್ನಡ",         romanised: "Kannada",        flag: "🇮🇳" },
  { code: "ml",    native: "മലയാളം",         romanised: "Malayalam",       flag: "🇮🇳" },
  { code: "gu",    native: "ગુજરાતી",         romanised: "Gujarati",        flag: "🇮🇳" },
  { code: "pa",    native: "ਪੰਜਾਬੀ",         romanised: "Punjabi",         flag: "🇮🇳" },
  { code: "or",    native: "ଓଡ଼ିଆ",            romanised: "Odia",            flag: "🇮🇳" },
  { code: "as",    native: "অসমীয়া",           romanised: "Assamese",        flag: "🇮🇳" },
  { code: "ur",    native: "اردو",              romanised: "Urdu",           flag: "🇮🇳", rtl: true },
  { code: "sd",    native: "سندھي",             romanised: "Sindhi",          flag: "🇮🇳", rtl: true },
  { code: "ks",    native: "کشمیری",            romanised: "Kashmiri",        flag: "🇮🇳", rtl: true },
  { code: "ne",    native: "नेपाली",            romanised: "Nepali",          flag: "🇮🇳" },
  { code: "mai",   native: "मैथिली",           romanised: "Maithili",        flag: "🇮🇳" },
  { code: "doi",   native: "डोगरी",             romanised: "Dogri",           flag: "🇮🇳" },
  { code: "kok",   native: "कोंकणी",            romanised: "Konkani",         flag: "🇮🇳" },
  { code: "mni",   native: "মৈতৈলোন",           romanised: "Manipuri",        flag: "🇮🇳" },
  { code: "sat",   native: "ᱥᱟᱱᱛᱟᱲᱤ",       romanised: "Santali",         flag: "🇮🇳" },
  { code: "san",   native: "संस्कृत",            romanised: "Sanskrit",        flag: "🇮🇳" },
  { code: "brx",   native: "बड़ो",               romanised: "Bodo",            flag: "🇮🇳" },
  { code: "hne",   native: "छत्तीसगढ़ी",        romanised: "Chhattisgarhi",   flag: "🇮🇳" },
];

const KNOWN_LANGS = [
  "en", "hi", "ta", "mr", "bn", "te", "kn", "ml", "gu", "pa",
  "or", "as", "ur", "sd", "ks", "ne", "mai", "doi", "kok", "mni",
  "sat", "san", "brx", "hne",
];

function detectBrowserLang() {
  const bl = navigator.language?.split("-")[0] || "en";
  return KNOWN_LANGS.includes(bl) ? bl : "en";
}

// ---------------------------------------------------------------------------
// CSS — injected once into document head
// ---------------------------------------------------------------------------

const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600;700&family=DM+Sans:wght@300;400;500;600&display=swap');

  @keyframes cb-orb1 {
    0%, 100% { transform: translate(0, 0) scale(1); }
    33%       { transform: translate(48px, -32px) scale(1.08); }
    66%       { transform: translate(-28px, 44px) scale(0.94); }
  }
  @keyframes cb-orb2 {
    0%, 100% { transform: translate(0, 0) scale(1); }
    33%       { transform: translate(-60px, 24px) scale(1.05); }
    66%       { transform: translate(36px, -52px) scale(0.97); }
  }
  @keyframes cb-orb3 {
    0%, 100% { transform: translate(0, 0) scale(1); }
    50%       { transform: translate(44px, 36px) scale(1.12); }
  }
  @keyframes cb-shimmer {
    0%   { opacity: 0; transform: translateY(-120%) rotate(25deg); }
    60%  { opacity: 0.7; }
    100% { opacity: 0; transform: translateY(250%) rotate(25deg); }
  }
  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  .cb-orb1 { animation: cb-orb1 12s ease-in-out infinite; }
  .cb-orb2 { animation: cb-orb2 18s ease-in-out infinite; animation-delay: -5s; }
  .cb-orb3 { animation: cb-orb3 22s ease-in-out infinite; animation-delay: -11s; }
`;

// ---------------------------------------------------------------------------
// ConsentBanner
// ---------------------------------------------------------------------------

export default function ConsentBanner() {
  const [visible, setVisible] = useState(false);
  const [lang, setLang] = useState("en");
  const [showLang, setShowLang] = useState(false);
  const [langSearch, setLangSearch] = useState("");
  const [showPrefs, setShowPrefs] = useState(false);
  const [selected, setSelected] = useState(new Set(["analytics", "marketing", "personalisation"]));
  const [loading, setLoading] = useState(false);
  const [shimmer, setShimmer] = useState(false);

  const cardRef = useRef(null);
  const rafRef = useRef(null);
  const langDropRef = useRef(null);

  const t = T[lang] || T.en;
  const langInfo = ALL_LANGS.find((l) => l.code === lang) || ALL_LANGS[0];
  const isRtl = langInfo.rtl || false;

  // Inject CSS once
  useEffect(() => {
    if (document.getElementById("cb-styles")) return;
    const s = document.createElement("style");
    s.id = "cb-styles";
    s.textContent = CSS;
    document.head.appendChild(s);
    return () => document.getElementById("cb-styles")?.remove();
  }, []);

  // Mount: check cookie, detect language
  useEffect(() => {
    const saved = localStorage.getItem("cb-lang");
    if (saved && KNOWN_LANGS.includes(saved)) setLang(saved);
    else setLang(detectBrowserLang());

    const hasConsent = Cookies.get("dpdp_consent_id") || Cookies.get("dpdp_consent_rejected");
    if (hasConsent) { setVisible(false); return; }
    setVisible(true);
  }, []);

  useEffect(() => {
    if (lang) localStorage.setItem("cb-lang", lang);
  }, [lang]);

  // 3D tilt — 60fps via rAF
  useEffect(() => {
    const card = cardRef.current;
    if (!card) return;

    const onMove = (e) => {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => {
        const rect = card.getBoundingClientRect();
        const cx = rect.left + rect.width / 2;
        const cy = rect.top + rect.height / 2;
        const dx = Math.max(-1, Math.min(1, (e.clientX - cx) / (rect.width / 2)));
        const dy = Math.max(-1, Math.min(1, -(e.clientY - cy) / (rect.height / 2)));
        card.style.transform = `perspective(900px) rotateX(${dy * 8}deg) rotateY(${dx * 8}deg)`;
        card.style.setProperty("--mx", `${e.clientX - rect.left}px`);
        card.style.setProperty("--my", `${e.clientY - rect.top}px`);
      });
    };

    const onLeave = () => {
      cancelAnimationFrame(rafRef.current);
      card.style.transform = "perspective(900px) rotateX(0deg) rotateY(0deg)";
    };

    card.addEventListener("mousemove", onMove);
    card.addEventListener("mouseleave", onLeave);
    return () => {
      card.removeEventListener("mousemove", onMove);
      card.removeEventListener("mouseleave", onLeave);
    };
  }, []);

  // Close lang dropdown on outside click / Escape
  useEffect(() => {
    if (!showLang) return;
    const onDown = (e) => {
      if (e.key === "Escape") { setShowLang(false); return; }
      if (langDropRef.current && !langDropRef.current.contains(e.target)) {
        setShowLang(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onDown);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onDown);
    };
  }, [showLang]);

  const handleLangChange = (code) => {
    const normalised = KNOWN_LANGS.includes(code) ? code : "en";
    setLang(normalised);
    setLangSearch("");
    setShowLang(false);
  };

  // Reset to defaults — used when user Cancel-s out of preferences
  const resetPreferences = () => {
    setSelected(new Set(["analytics", "marketing", "personalisation"]));
    setShowPrefs(false);
  };

  const togglePurpose = (code) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  };

  const emit = (state) => {
    window.dispatchEvent(new CustomEvent("ConsentUpdated", { detail: state }));
  };

  const userId = () => {
    try {
      const u = JSON.parse(localStorage.getItem("rdw_user") || "null");
      return u?.id || u?.sub || u?.email || `anon_${Date.now()}`;
    } catch { return `anon_${Date.now()}`; }
  };

  // Map banner purpose codes → backend DPDP purpose codes
  // Banner has 4 purposes; backend expects its own code set
  const BANNER_TO_BACKEND = {
    account_service:  "account_creation",
    analytics:        "analytics",
    marketing:        "marketing_email",
    personalisation: "marketing_email",
  };

  const ALL_BANNER_CODES = ["account_service", "analytics", "marketing", "personalisation"];
  const ALL_BACKEND_CODES = ALL_BANNER_CODES.map((c) => BANNER_TO_BACKEND[c]);

  const toBackendCodes = (bannerCodes) =>
    bannerCodes.map((c) => BANNER_TO_BACKEND[c]).filter(Boolean);

  const userIdVal = userId();

  // ── localStorage fallback helpers ────────────────────────────────────────
  // Used when the backend is unreachable (network error, 500, timeout)
  const LS_CONSENT_KEY = "dpdp_consent_fallback";

  const saveConsentLocally = (granted, purposes) => {
    localStorage.setItem(LS_CONSENT_KEY, JSON.stringify({
      granted,
      purposes,
      timestamp: Date.now(),
      userId: userIdVal,
    }));
  };

  const setConsentCookie = (granted) => {
    if (granted) {
      Cookies.set("dpdp_consent_id", `local_${Date.now()}`, {
        expires: 365, sameSite: "Strict",
        secure: window.location.protocol === "https:",
      });
    } else {
      Cookies.set("dpdp_consent_rejected", "1", {
        expires: 180, sameSite: "Strict",
        secure: window.location.protocol === "https:",
      });
    }
  };

  const finishConsent = (granted, purposes) => {
    setConsentCookie(granted);
    emit({ granted, purposes });
    setVisible(false);
  };

  // ── Accept All ────────────────────────────────────────────────────────────
  const handleAccept = useCallback(async () => {
    setLoading(true);
    setShimmer(true);
    try {
      const result = await grantConsent({
        data_principal_id: userIdVal,
        purposes: ALL_BACKEND_CODES,
        notice_id: "",
        channel: "web",
        is_minor: false,
      });
      setConsentCookie(true);
      Cookies.set("dpdp_consent_id", result.consent_id || "1", {
        expires: 365, sameSite: "Strict",
        secure: window.location.protocol === "https:",
      });
      emit({ granted: true, purposes: ALL_BANNER_CODES });
      setVisible(false);
    } catch (err) {
      console.warn("[Consent] Backend unavailable, saving locally:", err?.message || err);
      // Fallback: save locally, dismiss banner, show warning
      saveConsentLocally(true, ALL_BANNER_CODES);
      finishConsent(true, ALL_BANNER_CODES);
      toast.warning("Saved locally — will sync when connection is restored", {
        duration: 4000,
      });
    } finally {
      setLoading(false);
      setTimeout(() => setShimmer(false), 600);
    }
  }, [userIdVal]);

  // ── Reject Non-Essential ────────────────────────────────────────────────
  const handleReject = useCallback(async () => {
    setLoading(true);
    setShimmer(true);
    try {
      await withdrawConsent({
        data_principal_id: userIdVal,
        all_purposes: true,
      });
      setConsentCookie(false);
      emit({ granted: false, purposes: ["account_service"] });
      setVisible(false);
    } catch (err) {
      console.warn("[Consent] Backend unavailable, saving locally:", err?.message || err);
      saveConsentLocally(false, ["account_service"]);
      finishConsent(false, ["account_service"]);
      toast.warning("Saved locally — will sync when connection is restored", {
        duration: 4000,
      });
    } finally {
      setLoading(false);
      setTimeout(() => setShimmer(false), 600);
    }
  }, [userIdVal]);

  // ── Accept Selected ──────────────────────────────────────────────────────
  const handleAcceptSelected = useCallback(async () => {
    setLoading(true);
    setShimmer(true);
    try {
      const bannerCodes = ["account_service", ...Array.from(selected)];
      const result = await grantConsent({
        data_principal_id: userIdVal,
        purposes: toBackendCodes(bannerCodes),
        notice_id: "",
        channel: "web",
        is_minor: false,
      });
      setConsentCookie(true);
      Cookies.set("dpdp_consent_id", result.consent_id || "1", {
        expires: 365, sameSite: "Strict",
        secure: window.location.protocol === "https:",
      });
      emit({ granted: true, purposes: bannerCodes });
      setVisible(false);
    } catch (err) {
      console.warn("[Consent] Backend unavailable, saving locally:", err?.message || err);
      const bannerCodes = ["account_service", ...Array.from(selected)];
      saveConsentLocally(true, bannerCodes);
      finishConsent(true, bannerCodes);
      toast.warning("Saved locally — will sync when connection is restored", {
        duration: 4000,
      });
    } finally {
      setLoading(false);
      setTimeout(() => setShimmer(false), 600);
    }
  }, [userIdVal, selected]);

  if (!visible) return null;

  const filteredLangs = ALL_LANGS.filter(
    (l) => l.native.includes(langSearch) || l.romanised.toLowerCase().includes(langSearch.toLowerCase())
  );

  const purposes = [
    { code: "account_service",  label: t.p0Label, desc: t.p0Desc, required: true,  checked: true },
    { code: "analytics",         label: t.p1Label, desc: t.p1Desc, required: false, checked: selected.has("analytics") },
    { code: "marketing",          label: t.p2Label, desc: t.p2Desc, required: false, checked: selected.has("marketing") },
    { code: "personalisation",    label: t.p3Label, desc: t.p3Desc, required: false, checked: selected.has("personalisation") },
  ];

  const selectedCount = selected.size;

  // ─── Styles ────────────────────────────────────────────────────────────────

  const wrap = {
    position: "fixed", inset: 0, zIndex: 9998,
    background: "#04060f",
    display: "flex", alignItems: "flex-end", justifyContent: "flex-start",
    padding: "0 0 28px 28px", overflow: "hidden",
    fontFamily: "'DM Sans', sans-serif",
    direction: isRtl ? "rtl" : "ltr",
  };

  const orbBase = { position: "absolute", borderRadius: "50%", filter: "blur(60px)", opacity: 0.45, pointerEvents: "none" };

  const dotGrid = {
    position: "absolute", inset: 0,
    backgroundImage: "radial-gradient(circle, rgba(255,255,255,0.07) 1px, transparent 1px)",
    backgroundSize: "32px 32px", pointerEvents: "none",
  };

  const cardStyle = {
    position: "relative", width: "420px", maxWidth: "calc(100vw - 56px)",
    borderRadius: "20px", backdropFilter: "blur(24px)",
    background: "rgba(255,255,255,0.04)",
    border: "1px solid rgba(255,255,255,0.12)",
    boxShadow: "0 0 80px rgba(91,45,232,0.15), 0 24px 80px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.08)",
    transition: "transform 0.05s linear, box-shadow 0.3s ease",
    willChange: "transform", overflow: "hidden",
  };

  const headerStyle = { padding: "20px 24px 16px", borderBottom: "1px solid rgba(255,255,255,0.06)" };
  const bodyStyle   = { padding: "16px 24px", maxHeight: "460px", overflowY: "auto" };
  const footerStyle = { padding: "16px 24px 20px", borderTop: "1px solid rgba(255,255,255,0.06)", display: "flex", flexDirection: "column", gap: "8px" };

  const shimmerOverlay = {
    position: "absolute", inset: 0,
    background: "linear-gradient(135deg, rgba(255,255,255,0.08) 0%, transparent 60%)",
    borderRadius: "20px", pointerEvents: "none",
    opacity: shimmer ? 1 : 0, transition: "opacity 0.3s", zIndex: 10,
  };

  const dataRows = [
    { color: "#22c55e", label: t.retentionLabel,  value: t.retentionValue },
    { color: "#3b82f6", label: t.locationLabel,    value: t.locationValue },
    { color: "#f59e0b", label: t.thirdPartyLabel,   value: t.thirdPartyValue },
  ];

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <div style={wrap} role="dialog" aria-modal="true" aria-label={t.heading}>

      {/* Animated background orbs */}
      <div className="cb-orb1" style={{ ...orbBase, width: "500px", height: "500px", background: "radial-gradient(circle, #5b21b6 0%, transparent 70%)", top: "-15%", left: "-10%" }} />
      <div className="cb-orb2" style={{ ...orbBase, width: "600px", height: "600px", background: "radial-gradient(circle, #1e3a8a 0%, transparent 70%)", bottom: "-20%", right: "-5%" }} />
      <div className="cb-orb3" style={{ ...orbBase, width: "400px", height: "400px", background: "radial-gradient(circle, #4c1d95 0%, transparent 70%)", top: "30%", left: "20%" }} />

      {/* Dot grid */}
      <div style={dotGrid} />

      {/* 3D Glass Card */}
      <div ref={cardRef} style={cardStyle}>
        <div style={shimmerOverlay} />

        {/* ── Header ─────────────────────────────────── */}
        <div style={headerStyle}>
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "12px" }}>
            <div>
              <h2 style={{ fontFamily: "'Playfair Display', serif", fontWeight: 600, fontSize: "20px", color: "#f0eeff", margin: 0, lineHeight: 1.2 }}>
                {t.heading}
              </h2>
              <p style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300, fontSize: "12px", color: "rgba(255,255,255,0.45)", margin: "4px 0 0", letterSpacing: "0.04em" }}>
                {t.subheading}
              </p>
            </div>

            {/* Language selector */}
            <div style={{ position: "relative" }} ref={langDropRef}>
              <button
                onClick={() => setShowLang((v) => !v)}
                aria-label={t.selectLang}
                aria-expanded={showLang}
                style={{
                  display: "flex", alignItems: "center", gap: "6px",
                  padding: "6px 12px", borderRadius: "20px",
                  background: "rgba(255,255,255,0.06)",
                  border: "1px solid rgba(255,255,255,0.1)",
                  color: "#e2e8f0", fontFamily: "'DM Sans', sans-serif",
                  fontWeight: 400, fontSize: "12px", cursor: "pointer",
                  transition: "background 0.2s", whiteSpace: "nowrap",
                }}
              >
                <Globe size={13} />
                <span>{langInfo.flag}</span>
                <span>{langInfo.native}</span>
                <ChevronDown size={11} style={{ transform: showLang ? "rotate(180deg)" : "none", transition: "transform 0.2s" }} />
              </button>

              {/* Language dropdown */}
              <AnimatePresence>
                {showLang && (
                  <motion.div
                    initial={{ opacity: 0, y: 6, scale: 0.97 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 6, scale: 0.97 }}
                    transition={{ duration: 0.15 }}
                    style={{
                      position: "absolute", top: "calc(100% + 8px)",
                      [isRtl ? "left" : "right"]: 0,
                      width: "240px", borderRadius: "14px",
                      background: "#0d0f1e",
                      border: "1px solid rgba(91,45,232,0.3)",
                      boxShadow: "0 16px 40px rgba(0,0,0,0.5)",
                      overflow: "hidden", zIndex: 100,
                    }}
                  >
                    {/* Search */}
                    <div style={{ padding: "10px 12px", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "8px", padding: "7px 10px", borderRadius: "8px", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)" }}>
                        <Search size={12} style={{ color: "rgba(255,255,255,0.4)", flexShrink: 0 }} />
                        <input
                          value={langSearch}
                          onChange={(e) => setLangSearch(e.target.value)}
                          placeholder={t.searchPlaceholder}
                          autoFocus
                          style={{ background: "transparent", border: "none", outline: "none", color: "#e2e8f0", fontFamily: "'DM Sans', sans-serif", fontWeight: 300, fontSize: "12px", width: "100%" }}
                        />
                      </div>
                    </div>

                    {/* Language list */}
                    <div style={{ maxHeight: "260px", overflowY: "auto", padding: "4px 0" }}>
                      {filteredLangs.map((l) => (
                        <button
                          key={l.code}
                          onClick={() => handleLangChange(l.code)}
                          style={{
                            display: "flex", alignItems: "center", gap: "10px",
                            width: "100%", padding: "9px 14px",
                            background: lang === l.code ? "rgba(91,45,232,0.18)" : "transparent",
                            border: "none", cursor: "pointer",
                            transition: "background 0.15s",
                            textAlign: isRtl ? "right" : "left",
                          }}
                          onMouseEnter={(e) => { if (lang !== l.code) e.currentTarget.style.background = "rgba(255,255,255,0.04)"; }}
                          onMouseLeave={(e) => { if (lang !== l.code) e.currentTarget.style.background = "transparent"; }}
                        >
                          <span style={{ fontSize: "16px", flexShrink: 0 }}>{l.flag}</span>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 400, fontSize: "13px", color: lang === l.code ? "#c4b5fd" : "#e2e8f0", lineHeight: 1.3 }}>{l.native}</div>
                            <div style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300, fontSize: "11px", color: "rgba(255,255,255,0.4)" }}>{l.romanised}</div>
                          </div>
                          <span style={{ fontFamily: "monospace", fontSize: "10px", color: "rgba(255,255,255,0.3)", background: "rgba(255,255,255,0.05)", padding: "2px 5px", borderRadius: "4px", flexShrink: 0, letterSpacing: "0.03em" }}>
                            {l.code.toUpperCase()}
                          </span>
                          {lang === l.code && <Check size={13} style={{ color: "#818cf8", flexShrink: 0 }} />}
                        </button>
                      ))}
                      {filteredLangs.length === 0 && (
                        <div style={{ padding: "16px", textAlign: "center", color: "rgba(255,255,255,0.35)", fontFamily: "'DM Sans', sans-serif", fontSize: "12px" }}>
                          No languages found
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>

        {/* ── Body ─────────────────────────────────────── */}
        <div style={bodyStyle}>
          <AnimatePresence mode="wait">
            {showPrefs ? (
              <motion.div
                key="prefs"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.12 }}
              >
                <button
                  onClick={resetPreferences}
                  style={{ display: "flex", alignItems: "center", gap: "4px", background: "transparent", border: "none", color: "#818cf8", fontFamily: "'DM Sans', sans-serif", fontWeight: 400, fontSize: "12px", cursor: "pointer", padding: "0 0 12px", letterSpacing: "0.02em" }}
                >
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M7.5 2L3.5 6L7.5 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  {t.backBtn || "Back"}
                </button>

                {purposes.map((p) => (
                  <div key={p.code} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px", padding: "12px 14px", borderRadius: "12px", border: `1px solid ${p.checked ? "rgba(91,45,232,0.35)" : "rgba(255,255,255,0.07)"}`, background: p.checked ? "rgba(91,45,232,0.1)" : "rgba(255,255,255,0.02)", marginBottom: "8px", transition: "border-color 0.2s, background 0.2s" }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "2px" }}>
                        <span style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500, fontSize: "13px", color: "#e2e8f0" }}>{p.label}</span>
                        <span style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 400, fontSize: "10px", color: p.required ? "#818cf8" : "rgba(255,255,255,0.35)", background: p.required ? "rgba(91,45,232,0.15)" : "rgba(255,255,255,0.04)", padding: "1px 6px", borderRadius: "4px", letterSpacing: "0.03em" }}>
                          {p.required ? t.required : t.optional}
                        </span>
                      </div>
                      <p style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300, fontSize: "11px", color: "rgba(255,255,255,0.45)", margin: 0, lineHeight: 1.4 }}>{p.desc}</p>
                    </div>
                    {p.required ? (
                      <div style={{ width: "36px", height: "20px", borderRadius: "10px", background: "rgba(91,45,232,0.25)", border: "1px solid rgba(91,45,232,0.4)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <Lock size={10} style={{ color: "#818cf8" }} />
                      </div>
                    ) : (
                      <Switch checked={p.checked} onCheckedChange={() => togglePurpose(p.code)} style={{ scale: "0.85", transformOrigin: "right center" }} />
                    )}
                  </div>
                ))}

                {/* Data strip */}
                <div style={{ marginTop: "12px", padding: "12px 14px", borderRadius: "12px", background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}>
                  {dataRows.map((row) => (
                    <div key={row.label} style={{ display: "flex", alignItems: "center", gap: "10px", padding: "5px 0", borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
                      <div style={{ width: "8px", height: "8px", borderRadius: "2px", background: row.color, flexShrink: 0 }} />
                      <span style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300, fontSize: "11px", color: "rgba(255,255,255,0.45)", flex: "0 0 100px" }}>{row.label}</span>
                      <span style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 400, fontSize: "11px", color: "rgba(255,255,255,0.75)" }}>{row.value}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="main"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.12 }}
              >
                {/* Privacy notice */}
                <div style={{ padding: "12px 14px", borderRadius: "12px", background: "rgba(255,255,255,0.03)", border: "1px solid rgba(91,45,232,0.2)", marginBottom: "14px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "6px" }}>
                    <Lock size={12} style={{ color: "#818cf8" }} />
                    <span style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: "12px", color: "#818cf8", letterSpacing: "0.03em" }}>{t.privacyNotice}</span>
                  </div>
                  <p style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300, fontSize: "12px", color: "rgba(255,255,255,0.6)", lineHeight: 1.6, margin: 0 }}>{t.noticeText}</p>
                </div>

                {/* Purpose cards */}
                <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                  {purposes.map((p) => (
                    <div
                      key={p.code}
                      role={p.required ? undefined : "checkbox"}
                      aria-checked={p.checked}
                      aria-disabled={p.required}
                      tabIndex={p.required ? undefined : 0}
                      onClick={() => { if (!p.required) togglePurpose(p.code); }}
                      onKeyDown={(e) => { if (!p.required && (e.key === " " || e.key === "Enter")) { e.preventDefault(); togglePurpose(p.code); } }}
                      style={{
                        display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px",
                        padding: "11px 14px", borderRadius: "12px",
                        border: `1px solid ${p.checked ? "rgba(91,45,232,0.4)" : "rgba(255,255,255,0.07)"}`,
                        background: p.checked ? "rgba(91,45,232,0.1)" : "rgba(255,255,255,0.02)",
                        cursor: p.required ? "default" : "pointer",
                        transition: "border-color 0.2s, background 0.2s",
                        outline: "none",
                      }}
                      onMouseEnter={(e) => { if (!p.required && !p.checked) e.currentTarget.style.borderColor = "rgba(91,45,232,0.4)"; }}
                      onMouseLeave={(e) => { if (!p.required && !p.checked) e.currentTarget.style.borderColor = "rgba(255,255,255,0.07)"; }}
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "2px" }}>
                          <span style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500, fontSize: "13px", color: "#e2e8f0" }}>{p.label}</span>
                          {p.required && (
                            <span style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 400, fontSize: "10px", color: "#818cf8", background: "rgba(91,45,232,0.15)", padding: "1px 6px", borderRadius: "4px", letterSpacing: "0.03em" }}>
                              {t.required}
                            </span>
                          )}
                        </div>
                        <p style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300, fontSize: "11px", color: "rgba(255,255,255,0.45)", margin: 0, lineHeight: 1.4 }}>{p.desc}</p>
                      </div>
                      <div style={{ width: "20px", height: "20px", borderRadius: "6px", border: `1.5px solid ${p.checked ? "#818cf8" : "rgba(255,255,255,0.2)"}`, background: p.checked ? "rgba(91,45,232,0.3)" : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, transition: "border-color 0.2s, background 0.2s" }}>
                        {p.checked && <Check size={12} style={{ color: "#818cf8" }} />}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Data strip */}
                <div style={{ marginTop: "12px", padding: "10px 14px", borderRadius: "10px", background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}>
                  {dataRows.map((row) => (
                    <div key={row.label} style={{ display: "flex", alignItems: "center", gap: "8px", padding: "4px 0", borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
                      <div style={{ width: "7px", height: "7px", borderRadius: "2px", background: row.color, flexShrink: 0 }} />
                      <span style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300, fontSize: "11px", color: "rgba(255,255,255,0.45)", flex: "0 0 100px" }}>{row.label}</span>
                      <span style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 400, fontSize: "11px", color: "rgba(255,255,255,0.75)" }}>{row.value}</span>
                    </div>
                  ))}
                </div>

                <button
                  onClick={() => setShowPrefs(true)}
                  style={{ display: "flex", alignItems: "center", gap: "4px", marginTop: "10px", background: "transparent", border: "none", color: "#818cf8", fontFamily: "'DM Sans', sans-serif", fontWeight: 400, fontSize: "12px", cursor: "pointer", padding: "4px 0", letterSpacing: "0.02em" }}
                >
                  {t.customize}
                  <ChevronDown size={12} style={{ transform: "rotate(-90deg)" }} />
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* ── Footer ───────────────────────────────────── */}
        <div style={footerStyle}>
          <AnimatePresence mode="wait">
            {showPrefs ? (
              <motion.div key="prefs-actions" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.12 }}>
                <button
                  onClick={handleAcceptSelected}
                  disabled={loading}
                  style={{ width: "100%", height: "44px", borderRadius: "12px", background: loading ? "rgba(91,45,232,0.5)" : "linear-gradient(135deg, #5b2de8 0%, #2563eb 100%)", border: "none", color: "#fff", fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: "14px", cursor: loading ? "not-allowed" : "pointer", letterSpacing: "0.02em", transition: "background 0.2s", boxShadow: "0 4px 20px rgba(91,45,232,0.3)", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" }}
                  onMouseEnter={(e) => { if (!loading) e.currentTarget.style.boxShadow = "0 6px 28px rgba(91,45,232,0.5)"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "0 4px 20px rgba(91,45,232,0.3)"; }}
                >
                  {loading ? (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ animation: "spin 0.8s linear infinite" }}><circle cx="12" cy="12" r="10" stroke="rgba(255,255,255,0.3)" strokeWidth="3"/><path d="M12 2a10 10 0 0 1 10 10" stroke="white" strokeWidth="3" strokeLinecap="round"/></svg>
                  ) : null}
                  {loading ? "Saving..." : t.acceptSelected(selectedCount)}
                </button>
                <button
                  onClick={resetPreferences}
                  style={{ width: "100%", height: "40px", borderRadius: "10px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", color: "rgba(255,255,255,0.55)", fontFamily: "'DM Sans', sans-serif", fontWeight: 400, fontSize: "13px", cursor: "pointer" }}
                >
                  {t.backBtn || "Back"}
                </button>
              </motion.div>
            ) : (
              <motion.div key="main-actions" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.12 }}>
                {/* Primary gradient */}
                <button
                  onClick={handleAccept}
                  disabled={loading}
                  style={{ width: "100%", height: "46px", borderRadius: "12px", background: loading ? "rgba(91,45,232,0.5)" : "linear-gradient(135deg, #5b2de8 0%, #2563eb 100%)", border: "none", color: "#fff", fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: "14px", cursor: loading ? "not-allowed" : "pointer", letterSpacing: "0.02em", boxShadow: "0 4px 20px rgba(91,45,232,0.3)", transition: "background 0.2s, box-shadow 0.2s" }}
                  onMouseEnter={(e) => { if (!loading) e.currentTarget.style.boxShadow = "0 6px 28px rgba(91,45,232,0.5)"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "0 4px 20px rgba(91,45,232,0.3)"; }}
                >
                  {loading ? "Saving..." : (t.acceptAllBtn || "Accept All & Continue")}
                </button>

                {/* Secondary row */}
                <div style={{ display: "flex", gap: "8px" }}>
                  <button
                    onClick={() => setShowPrefs(true)}
                    style={{ flex: 1, height: "40px", borderRadius: "10px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", color: "#e2e8f0", fontFamily: "'DM Sans', sans-serif", fontWeight: 400, fontSize: "13px", cursor: "pointer", transition: "border-color 0.2s, background 0.2s" }}
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = "rgba(91,45,232,0.3)"; e.currentTarget.style.background = "rgba(91,45,232,0.08)"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.08)"; e.currentTarget.style.background = "rgba(255,255,255,0.04)"; }}
                  >
                    {t.manageBtn}
                  </button>
                  <button
                    onClick={handleReject}
                    disabled={loading}
                    style={{ flex: 1, height: "40px", borderRadius: "10px", background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.2)", color: "#fca5a5", fontFamily: "'DM Sans', sans-serif", fontWeight: 400, fontSize: "13px", cursor: loading ? "not-allowed" : "pointer", transition: "border-color 0.2s, background 0.2s" }}
                    onMouseEnter={(e) => { if (!loading) { e.currentTarget.style.background = "rgba(239,68,68,0.15)"; e.currentTarget.style.borderColor = "rgba(239,68,68,0.35)"; } }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(239,68,68,0.08)"; e.currentTarget.style.borderColor = "rgba(239,68,68,0.2)"; }}
                  >
                    {t.rejectBtn}
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Footer links */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "12px", marginTop: "4px" }}>
            {[
              { label: "Privacy Centre",   href: "/privacy-centre" },
              { label: "Privacy Policy",   href: "/privacy-policy" },
              { label: "Contact DPO",      href: "mailto:dpo@rightdoersworld.com" },
            ].map((link) => (
              <a
                key={link.label}
                href={link.href}
                style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300, fontSize: "11px", color: "rgba(255,255,255,0.3)", textDecoration: "none", letterSpacing: "0.02em", transition: "color 0.2s" }}
                onMouseEnter={(e) => { e.currentTarget.style.color = "rgba(255,255,255,0.6)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.color = "rgba(255,255,255,0.3)"; }}
              >
                {link.label}
              </a>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
